/* iter55z-e — Smart-recommend a cheaper model for light-touch edits.
 *
 * Writers doing lots of small edits (tone-shift, continue, revise) on
 * a premium model pay a large fixed per-call cost for what is often a
 * ~200-word output. This chip appears in the editor toolbar when the
 * active model has a `cost_multiplier >= 3` (Opus-tier). One click
 * switches the project to the cheapest enabled sibling and toasts the
 * estimated savings.
 *
 * We deliberately keep it OFF for full-chapter drafting and novel
 * builds — those benefit from the premium model's prose quality.
 */
import React, { useEffect, useState } from 'react';
import { usePW } from '@/lib/store';
import { modelsApi } from '@/lib/api';
import { toast } from 'sonner';
import { Lightbulb, ArrowRight, X } from 'lucide-react';

const OPUS_TIER_THRESHOLD = 3;   // cost_multiplier >= 3 counts as premium
const TOKENS_PER_USD  = 60_000;
const TOKENS_PER_WORD = 1.3;

// Estimate the USD cost of a small edit at `words` output length for
// a given cost_multiplier.
function estCost(words, mult) {
  return ((words * TOKENS_PER_WORD) / TOKENS_PER_USD) * (Number(mult) || 1);
}

const fmtUsd = (n) => n < 0.01 ? `<$0.01` : `~$${n.toFixed(2)}`;

export function SmartModelHint() {
  const { project, updateProject } = usePW();
  const [models, setModels] = useState([]);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    modelsApi.list()
      .then((r) => { if (!cancelled) setModels(r.models || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Reset the dismissal when the user switches project or model manually.
  useEffect(() => { setDismissed(false); }, [project?.id, project?.model_id]);

  if (!project || models.length < 2 || dismissed) return null;

  const active = models.find((m) => m.id === project.model_id);
  if (!active || (active.cost_multiplier || 1) < OPUS_TIER_THRESHOLD) return null;

  // Find the cheapest ENABLED model that isn't the active one. Skip
  // "outline"-only families if the catalog tags them; otherwise the
  // cheapest option wins.
  const cheaper = [...models]
    .filter((m) => m.id !== active.id)
    .sort((a, b) => (a.cost_multiplier || 1) - (b.cost_multiplier || 1))[0];
  if (!cheaper) return null;
  if ((cheaper.cost_multiplier || 1) >= (active.cost_multiplier || 1) * 0.6) {
    // Not enough headroom — the cheaper model is barely cheaper (< 40 %).
    return null;
  }

  // Estimate savings on a ~200-word small edit (typical tone-shift / continue).
  const SMALL_EDIT_WORDS = 200;
  const activeCost  = estCost(SMALL_EDIT_WORDS, active.cost_multiplier);
  const cheaperCost = estCost(SMALL_EDIT_WORDS, cheaper.cost_multiplier);
  const savings     = activeCost - cheaperCost;
  const pct         = activeCost > 0 ? Math.round((savings / activeCost) * 100) : 0;

  const swap = async () => {
    try {
      await updateProject({ model_id: cheaper.id });
      toast.success(
        `Switched to ${cheaper.label} — small edits now cost ~${pct}% less.`,
      );
    } catch {
      toast.error('Could not switch model.');
    }
  };

  return (
    <div
      className="mx-2 mt-1 flex items-center gap-1.5 rounded-sm border border-amber-300/60 bg-amber-50 px-2 py-1 text-[13px] text-amber-900"
      data-testid="smart-model-hint"
    >
      <Lightbulb className="h-3 w-3 shrink-0" />
      <span>
        On <b>{active.label}</b>, a small edit runs {fmtUsd(activeCost)}.
        Try <b>{cheaper.label}</b> for {fmtUsd(cheaperCost)} (
        <span className="tabular-nums">–{pct}%</span>).
      </span>
      <button
        type="button"
        onClick={swap}
        className="ml-1 inline-flex items-center gap-0.5 rounded-sm bg-amber-200 px-1.5 py-0.5 text-[13px] font-semibold hover:bg-amber-300"
        data-testid="smart-model-hint-switch"
        title={`Switch project to ${cheaper.label}`}
      >
        Switch <ArrowRight className="h-2.5 w-2.5" />
      </button>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        className="inline-flex h-4 w-4 items-center justify-center rounded-sm text-amber-800/70 hover:bg-amber-200"
        data-testid="smart-model-hint-dismiss"
        title="Dismiss until I change the model"
      >
        <X className="h-2.5 w-2.5" />
      </button>
    </div>
  );
}
